import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronRight, Crosshair } from 'lucide-react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { cn } from '@rudi/ui/lib/utils';
import { PageHeader, QueryState } from '../components/page';
import { Meter } from '../components/Meter';
import { Sparkline } from '../components/Sparkline';
import {
  useClusters,
  type ClusterView,
  type GpuReport,
  type LoadAverage,
  type NodeReport,
  type PodReport,
  type UsageSample,
} from '../api/clusters';
import {
  activePodsOn,
  attention,
  clusterTotals,
  filterPods,
  formatBytes,
  formatCores,
  formatPercent,
  groupPods,
  hasProblem,
  narrowsPods,
  plural,
  trend,
  type ClustersSearch,
  type PodGroup,
} from './cluster-summary';
import { PodTable } from './cluster-pods';
import { NodeDisks } from './node-disks';

export { formatBytes, formatCores, validateClustersSearch } from './cluster-summary';

const UNSCHEDULED = 'Unscheduled';
const control = 'rounded-3xl border bg-background px-3 py-2 text-sm';

function ago(timestamp: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(timestamp)) / 1000));
  if (seconds < 90) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min ago`;
  return new Date(timestamp).toLocaleString();
}

const STATUS = {
  reporting: { label: 'Reporting', variant: 'default' },
  unreachable: { label: 'Unreachable', variant: 'destructive' },
  waiting: { label: 'Waiting for first report', variant: 'outline' },
} as const;

/**
 * Which rows are open. A row opens by default when the filters point at it;
 * a click overrides that until the filters change.
 */
function useOpenRows(filterKey: string) {
  const [state, setState] = useState<{ key: string; rows: Map<string, boolean> }>({
    key: filterKey,
    rows: new Map(),
  });
  const rows = state.key === filterKey ? state.rows : new Map<string, boolean>();
  return {
    isOpen: (row: string, byDefault: boolean) => rows.get(row) ?? byDefault,
    toggle: (row: string, byDefault: boolean) =>
      setState({ key: filterKey, rows: new Map(rows).set(row, !(rows.get(row) ?? byDefault)) }),
  };
}
type OpenRows = ReturnType<typeof useOpenRows>;

/** A row that opens and closes its children; they render only while open. */
function Disclosure({
  id,
  open,
  onToggle,
  title,
  summary,
  actions,
  children,
  className,
}: {
  id: string;
  open: boolean;
  onToggle: () => void;
  title: ReactNode;
  summary?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={onToggle}
          className="flex min-w-0 items-start gap-2 text-left"
        >
          <ChevronRight
            aria-hidden
            className={cn('mt-0.5 h-4 w-4 shrink-0 transition-transform', open && 'rotate-90')}
          />
          <span className="min-w-0">{title}</span>
        </button>
        {actions && <div className="ml-auto flex items-center gap-1">{actions}</div>}
        {summary && <div className="w-full">{summary}</div>}
      </div>
      {open && (
        <div id={id} className="mt-4">
          {children}
        </div>
      )}
    </div>
  );
}

function FocusButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      aria-label={`Show only ${label}`}
      title={`Show only ${label}`}
      onClick={onClick}
    >
      <Crosshair className="h-4 w-4" aria-hidden />
    </Button>
  );
}

/** Load averages to two decimals, as `uptime` prints them. */
function LoadAverages({ load }: { load: LoadAverage | null }) {
  const windows = load
    ? ([
        ['1 min', load.one],
        ['5 min', load.five],
        ['15 min', load.fifteen],
      ] as const)
    : null;
  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">Load</span>
        {!windows && <span className="text-muted-foreground">No data</span>}
      </div>
      {windows && (
        <dl className="grid grid-cols-3 gap-2 tabular-nums">
          {windows.map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd>{value.toFixed(2)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/** One GPU: how busy it is and how much of its memory is in use. */
function Gpu({ gpu }: { gpu: GpuReport }) {
  const label = `GPU ${gpu.index}`;
  const power =
    gpu.powerWatts === null
      ? null
      : `${Math.round(gpu.powerWatts)}${gpu.powerLimitWatts ? ` of ${Math.round(gpu.powerLimitWatts)}` : ''} W`;
  const temperature =
    gpu.temperatureCelsius === null ? null : `${Math.round(gpu.temperatureCelsius)} °C`;
  return (
    <div className="space-y-2">
      <Meter
        label={label}
        used={gpu.utilizationPercent}
        total={100}
        format={formatPercent}
        summary={
          gpu.utilizationPercent === null
            ? undefined
            : `${formatPercent(gpu.utilizationPercent)} busy`
        }
        detail={[gpu.name, temperature, power].filter(Boolean).join(' · ')}
      />
      <Meter
        label={`${label} memory`}
        used={gpu.memoryUsedBytes}
        total={gpu.memoryTotalBytes}
        format={formatBytes}
      />
    </div>
  );
}

/**
 * Meter text for a narrow column: the percentage beside the label and the
 * figures under the bar, so neither wraps.
 */
function compact(
  used: number | null,
  total: number,
  format: (value: number) => string,
  note?: string
) {
  if (used === null || total <= 0) return { detail: note };
  return {
    summary: formatPercent((used / total) * 100),
    detail: [`${format(used)} of ${format(total)}`, note].filter(Boolean).join(' · '),
  };
}

/** A meter with the figure's last hour under it. */
function TrendMeter({
  label,
  used,
  total,
  format,
  detail,
  history,
  nodes,
  figure,
  height,
}: {
  label: string;
  used: number | null;
  total: number;
  format: (value: number) => string;
  detail?: string;
  history: UsageSample[];
  nodes: string[];
  figure: 'cpuMillicores' | 'memoryBytes';
  height?: number;
}) {
  return (
    <div className="space-y-2">
      <Meter
        label={label}
        used={used}
        total={total}
        format={format}
        {...compact(used, total, format, detail)}
      />
      <Sparkline
        label={`${label} trend`}
        points={trend(history, nodes, figure)}
        max={total}
        format={format}
        height={height}
      />
    </div>
  );
}

/** The figures a node row shows while closed. */
function NodeSummary({ node, pods }: { node: NodeReport; pods: PodReport[] }) {
  const cpu = node.usage?.cpuMillicores ?? null;
  const memory = node.usage?.memoryBytes ?? null;
  const active = activePodsOn(node.name, pods);
  return (
    // Usage is the whole node's, system daemons included, so it is measured
    // against capacity. Allocatable leaves out what the kubelet reserves.
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Meter
        label="CPU"
        used={cpu}
        total={node.capacity.cpuMillicores}
        format={formatCores}
        {...compact(cpu, node.capacity.cpuMillicores, formatCores)}
      />
      <Meter
        label="Memory"
        used={memory}
        total={node.capacity.memoryBytes}
        format={formatBytes}
        {...compact(memory, node.capacity.memoryBytes, formatBytes)}
      />
      <NodeDisks node={node} />
      <Meter
        label="Pods"
        used={active}
        total={node.allocatable.pods}
        format={String}
        {...compact(active, node.allocatable.pods, String)}
      />
    </div>
  );
}

/** What a node row shows once open, above the node's namespaces. */
function NodeDetails({ node, history }: { node: NodeReport; history: UsageSample[] }) {
  return (
    <div className="grid gap-x-6 gap-y-4 rounded-3xl bg-muted/40 p-4 md:grid-cols-2">
      <Meter
        label="CPU requested"
        used={node.requested.cpuMillicores}
        total={node.allocatable.cpuMillicores}
        format={formatCores}
        summary={formatPercent(
          (node.requested.cpuMillicores / node.allocatable.cpuMillicores) * 100
        )}
        detail={`${formatCores(node.requested.cpuMillicores)} requested of ${formatCores(node.allocatable.cpuMillicores)} allocatable`}
      />
      <Meter
        label="Memory requested"
        used={node.requested.memoryBytes}
        total={node.allocatable.memoryBytes}
        format={formatBytes}
        summary={formatPercent((node.requested.memoryBytes / node.allocatable.memoryBytes) * 100)}
        detail={`${formatBytes(node.requested.memoryBytes)} requested of ${formatBytes(node.allocatable.memoryBytes)} allocatable`}
      />
      <div className="space-y-1">
        <p className="text-sm font-medium">CPU, last hour</p>
        <Sparkline
          label={`${node.name} CPU`}
          points={trend(history, [node.name], 'cpuMillicores')}
          max={node.capacity.cpuMillicores}
          format={formatCores}
          height={48}
        />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">Memory, last hour</p>
        <Sparkline
          label={`${node.name} memory`}
          points={trend(history, [node.name], 'memoryBytes')}
          max={node.capacity.memoryBytes}
          format={formatBytes}
          height={48}
        />
      </div>
      <LoadAverages load={node.load ?? null} />
      {node.disks == null && node.imageFilesystem && (
        <Meter
          label="Container images"
          used={node.imageFilesystem.usedBytes}
          total={node.imageFilesystem.capacityBytes}
          format={formatBytes}
        />
      )}
      {/* Only nodes with a GPU show one. An older Receptor sends no GPU list. */}
      {node.gpus?.map(gpu => (
        <Gpu key={gpu.index} gpu={gpu} />
      ))}
    </div>
  );
}

function groupFigures(group: PodGroup): string {
  return [
    plural(group.pods.length, 'pod'),
    group.problems ? attention(group.problems) : null,
    group.restarts ? plural(group.restarts, 'restart') : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A namespace's pods: a row with their summed usage that opens to the pod table. */
function NamespaceRow({
  id,
  group,
  showNode,
  open,
  onToggle,
  onFocus,
}: {
  id: string;
  group: PodGroup;
  showNode: boolean;
  open: boolean;
  onToggle: () => void;
  onFocus: () => void;
}) {
  return (
    <Disclosure
      id={id}
      open={open}
      onToggle={onToggle}
      className="py-3"
      title={
        <>
          <span className="font-medium">{group.key}</span>
          <span
            className={cn(
              'block text-xs',
              group.problems ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {groupFigures(group)}
          </span>
        </>
      }
      actions={
        <>
          <div className="text-right text-xs tabular-nums text-muted-foreground">
            <div>
              CPU {group.usage ? formatCores(group.usage.cpuMillicores) : '–'} · requested{' '}
              {formatCores(group.requests.cpuMillicores)}
            </div>
            <div>
              Memory {group.usage ? formatBytes(group.usage.memoryBytes) : '–'} · requested{' '}
              {formatBytes(group.requests.memoryBytes)}
            </div>
          </div>
          <FocusButton label={`namespace ${group.key}`} onClick={onFocus} />
        </>
      }
    >
      <PodTable pods={group.pods} showNode={showNode} />
    </Disclosure>
  );
}

function NamespaceList({
  rowKey,
  clusterId,
  pods,
  showNode,
  search,
  rows,
  onSearch,
}: {
  rowKey: string;
  clusterId: string;
  pods: PodReport[];
  showNode: boolean;
  search: ClustersSearch;
  rows: OpenRows;
  onSearch: (next: ClustersSearch) => void;
}) {
  const groups = useMemo(() => groupPods(pods, pod => pod.namespace), [pods]);
  if (!groups.length)
    return (
      <p className="py-3 text-sm text-muted-foreground">
        {narrowsPods(search) ? 'No pods match the filters.' : 'No pods.'}
      </p>
    );
  return (
    <div className="divide-y">
      {groups.map(group => {
        const key = `${rowKey}/ns:${group.key}`;
        const byDefault = search.namespace === group.key || narrowsPods(search);
        return (
          <NamespaceRow
            key={group.key}
            id={key}
            group={group}
            showNode={showNode}
            open={rows.isOpen(key, byDefault)}
            onToggle={() => rows.toggle(key, byDefault)}
            onFocus={() => onSearch({ ...search, cluster: clusterId, namespace: group.key })}
          />
        );
      })}
    </div>
  );
}

function NodeTitle({ node, pods }: { node: NodeReport; pods: PodReport[] }) {
  const problems = pods.filter(hasProblem).length;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span>
        <h3 className="font-semibold">{node.name}</h3>
        <span className="block text-xs text-muted-foreground">
          {[node.roles.join(', ') || 'worker', node.kubeletVersion, node.osImage]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <span className="flex flex-wrap gap-1">
        <Badge variant={node.ready ? 'secondary' : 'destructive'}>
          {node.ready ? 'Ready' : 'Not ready'}
        </Badge>
        {node.unschedulable && <Badge variant="outline">Cordoned</Badge>}
        {node.pressure.map(condition => (
          <Badge key={condition} variant="destructive">
            {condition}
          </Badge>
        ))}
        {problems > 0 && <Badge variant="destructive">{attention(problems)}</Badge>}
      </span>
    </span>
  );
}

function ClusterBody({
  cluster,
  pods,
  search,
  rows,
  onSearch,
}: {
  cluster: ClusterView;
  pods: PodReport[];
  search: ClustersSearch;
  rows: OpenRows;
  onSearch: (next: ClustersSearch) => void;
}) {
  const report = cluster.report!;
  const rowKey = `c:${cluster.id}`;
  if (search.group === 'namespace')
    return (
      <NamespaceList
        rowKey={rowKey}
        clusterId={cluster.id}
        pods={pods}
        showNode
        search={search}
        rows={rows}
        onSearch={onSearch}
      />
    );

  const byNode = new Map<string, PodReport[]>();
  for (const pod of pods) {
    const node = pod.node ?? UNSCHEDULED;
    byNode.set(node, [...(byNode.get(node) ?? []), pod]);
  }
  const nodes = report.nodes.filter(
    node =>
      (!search.node || node.name === search.node) && (!narrowsPods(search) || byNode.has(node.name))
  );
  const unscheduled = search.node ? [] : (byNode.get(UNSCHEDULED) ?? []);
  if (!nodes.length && !unscheduled.length)
    return <p className="text-sm text-muted-foreground">No nodes or pods match the filters.</p>;
  return (
    <div className="divide-y rounded-3xl border">
      {nodes.map(node => {
        const key = `${rowKey}/n:${node.name}`;
        const byDefault = search.node === node.name || narrowsPods(search);
        const nodePods = byNode.get(node.name) ?? [];
        return (
          <Disclosure
            key={node.name}
            id={key}
            className="p-4"
            open={rows.isOpen(key, byDefault)}
            onToggle={() => rows.toggle(key, byDefault)}
            title={<NodeTitle node={node} pods={nodePods} />}
            actions={
              <FocusButton
                label={`node ${node.name}`}
                onClick={() => onSearch({ ...search, cluster: cluster.id, node: node.name })}
              />
            }
            summary={<NodeSummary node={node} pods={report.pods} />}
          >
            <div className="space-y-2">
              <NodeDetails node={node} history={cluster.history} />
              <NamespaceList
                rowKey={key}
                clusterId={cluster.id}
                pods={nodePods}
                showNode={false}
                search={search}
                rows={rows}
                onSearch={onSearch}
              />
            </div>
          </Disclosure>
        );
      })}
      {unscheduled.length > 0 && (
        <div className="p-4">
          <h3 className="font-semibold">{UNSCHEDULED}</h3>
          <p className="mb-2 text-xs text-muted-foreground">Pods no node has taken yet.</p>
          <PodTable pods={unscheduled} showNode={false} />
        </div>
      )}
    </div>
  );
}

/** The whole cluster at a glance: what its header shows while closed. */
function ClusterSummary({ cluster }: { cluster: ClusterView }) {
  const report = cluster.report!;
  const totals = clusterTotals(report.nodes, report.pods);
  const reporting = report.nodes.filter(node => node.usage).map(node => node.name);
  const missing = totals.nodesWithoutUsage
    ? `${plural(totals.nodesWithoutUsage, 'node')} without usage left out`
    : undefined;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <TrendMeter
        label="CPU"
        used={totals.cpu.used}
        total={totals.cpu.total}
        format={formatCores}
        detail={missing}
        history={cluster.history}
        nodes={reporting}
        figure="cpuMillicores"
      />
      <TrendMeter
        label="Memory"
        used={totals.memory.used}
        total={totals.memory.total}
        format={formatBytes}
        detail={missing}
        history={cluster.history}
        nodes={reporting}
        figure="memoryBytes"
      />
      <Meter
        label="Root disks"
        used={totals.disk.used}
        total={totals.disk.total}
        format={formatBytes}
        {...compact(
          totals.disk.used,
          totals.disk.total,
          formatBytes,
          'reported host root filesystems'
        )}
      />
      <Meter
        label="Pods"
        used={totals.pods.used}
        total={totals.pods.total}
        format={String}
        {...compact(
          totals.pods.used,
          totals.pods.total,
          String,
          totals.problems ? attention(totals.problems) : 'all running'
        )}
      />
    </div>
  );
}

function ClusterPanel({
  cluster,
  staleAfterSeconds,
  search,
  rows,
  onSearch,
}: {
  cluster: ClusterView;
  staleAfterSeconds: number;
  search: ClustersSearch;
  rows: OpenRows;
  onSearch: (next: ClustersSearch) => void;
}) {
  const status = STATUS[cluster.status];
  const report = cluster.report;
  const pods = useMemo(() => filterPods(report?.pods ?? [], search), [report, search]);
  const key = `c:${cluster.id}`;
  const byDefault = search.cluster === cluster.id || Boolean(search.node) || narrowsPods(search);
  const totals = report ? clusterTotals(report.nodes, report.pods) : null;
  return (
    <section aria-labelledby={`cluster-${cluster.id}`} className="rounded-3xl border p-5">
      <Disclosure
        id={key}
        open={report !== null && rows.isOpen(key, byDefault)}
        onToggle={() => report && rows.toggle(key, byDefault)}
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 id={`cluster-${cluster.id}`} className="text-xl font-semibold">
              {cluster.label}
            </h2>
            <Badge variant={status.variant}>{status.label}</Badge>
            {totals && (
              <span className="text-sm text-muted-foreground">
                {totals.readyNodes} of {plural(totals.nodes, 'node')} ready
              </span>
            )}
            {cluster.receivedAt && (
              <span className="text-sm text-muted-foreground">
                Last report {ago(cluster.receivedAt, Date.now())}
              </span>
            )}
          </span>
        }
        actions={
          report && (
            <FocusButton
              label={`cluster ${cluster.label}`}
              onClick={() => onSearch({ ...search, cluster: cluster.id, node: undefined })}
            />
          )
        }
        summary={
          <div className="space-y-4">
            {cluster.status === 'waiting' && (
              <p className="text-sm text-muted-foreground">
                This Cortex accepts reports from the {cluster.id} Receptor but has not received one
                since it started.
              </p>
            )}
            {cluster.status === 'unreachable' && (
              <p className="text-sm text-destructive">
                No report for more than {staleAfterSeconds} seconds. The Receptor, the cluster or
                the network between them may be down. The data below is from the last report.
              </p>
            )}
            {report && report.failures.length > 0 && (
              <div className="rounded-3xl border border-destructive/40 p-4 text-sm">
                <p className="font-medium">Some data is missing from this report</p>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {report.failures.map(failure => (
                    <li key={failure}>{failure}</li>
                  ))}
                </ul>
              </div>
            )}
            {report && (
              <div className={cn(cluster.status === 'unreachable' && 'opacity-60')}>
                <ClusterSummary cluster={cluster} />
              </div>
            )}
          </div>
        }
      >
        <div className={cn(cluster.status === 'unreachable' && 'opacity-60')}>
          <ClusterBody
            cluster={cluster}
            pods={pods}
            search={search}
            rows={rows}
            onSearch={onSearch}
          />
        </div>
      </Disclosure>
    </section>
  );
}

function unique(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

function Filters({
  clusters,
  search,
  onSearch,
}: {
  clusters: ClusterView[];
  search: ClustersSearch;
  onSearch: (next: ClustersSearch) => void;
}) {
  const inCluster = clusters.filter(cluster => !search.cluster || cluster.id === search.cluster);
  const reports = inCluster.flatMap(cluster => (cluster.report ? [cluster.report] : []));
  const nodes = unique(reports.flatMap(report => report.nodes.map(node => node.name)));
  const scoped = reports.flatMap(report =>
    report.pods.filter(pod => !search.node || pod.node === search.node)
  );
  const namespaces = unique(scoped.map(pod => pod.namespace));
  // The toggles count what they would show with the other filters kept.
  const matching = filterPods(scoped, { ...search, attention: false, failed: false });
  const problems = matching.filter(hasProblem).length;
  const failed = filterPods(scoped, { ...search, attention: false, failed: true }).length;
  const filtered = Boolean(search.cluster || search.node || narrowsPods(search));
  const set = (patch: Partial<ClustersSearch>) => onSearch({ ...search, ...patch });

  return (
    <div className="mb-6 flex flex-wrap items-center gap-2">
      <select
        aria-label="Cluster"
        className={control}
        value={search.cluster ?? ''}
        onChange={event => set({ cluster: event.target.value || undefined, node: undefined })}
      >
        <option value="">All clusters</option>
        {clusters.map(cluster => (
          <option key={cluster.id} value={cluster.id}>
            {cluster.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Node"
        className={control}
        value={search.node ?? ''}
        onChange={event => set({ node: event.target.value || undefined })}
      >
        <option value="">All nodes</option>
        {nodes.map(node => (
          <option key={node} value={node}>
            {node}
          </option>
        ))}
      </select>
      <select
        aria-label="Namespace"
        className={control}
        value={search.namespace ?? ''}
        onChange={event => set({ namespace: event.target.value || undefined })}
      >
        <option value="">All namespaces</option>
        {/* Keep a namespace from the URL listed even when no pod is in it now. */}
        {unique([...namespaces, ...(search.namespace ? [search.namespace] : [])]).map(name => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <Input
        value={search.q ?? ''}
        onChange={event => set({ q: event.target.value || undefined })}
        placeholder="Filter by pod, workload, namespace or node"
        aria-label="Filter pods"
        className="max-w-xs"
      />
      <Button
        variant={search.attention ? 'default' : 'outline'}
        aria-pressed={Boolean(search.attention)}
        disabled={search.failed}
        onClick={() => set({ attention: search.attention ? undefined : true })}
      >
        Needs attention ({problems})
      </Button>
      {(failed > 0 || search.failed) && (
        <Button
          variant={search.failed ? 'default' : 'outline'}
          aria-pressed={Boolean(search.failed)}
          onClick={() => set({ failed: search.failed ? undefined : true })}
        >
          Failed pods ({failed})
        </Button>
      )}
      <div role="group" aria-label="Group pods by" className="inline-flex rounded-3xl border p-0.5">
        {(['node', 'namespace'] as const).map(group => {
          const active = (search.group ?? 'node') === group;
          return (
            <button
              key={group}
              type="button"
              aria-pressed={active}
              onClick={() => set({ group: group === 'node' ? undefined : group })}
              className={cn(
                'rounded-3xl px-3 py-1.5 text-sm',
                active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              )}
            >
              By {group}
            </button>
          );
        })}
      </div>
      {filtered && (
        <Button variant="ghost" onClick={() => onSearch({ group: search.group })}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

/** The page without the router, so tests can drive the filters. */
export function ClustersView({
  search,
  onSearch,
}: {
  search: ClustersSearch;
  onSearch: (next: ClustersSearch) => void;
}) {
  const { data, isLoading, error } = useClusters();
  const rows = useOpenRows(
    JSON.stringify([
      search.cluster,
      search.node,
      search.namespace,
      search.q,
      search.attention,
      search.failed,
      search.group,
    ])
  );
  const clusters = (data?.clusters ?? []).filter(
    cluster => !search.cluster || cluster.id === search.cluster
  );
  return (
    <div>
      <PageHeader
        title="Clusters"
        description="Live node and pod usage reported by each cluster's Receptor. Refreshes every 15 seconds; the trends cover the last hour."
      />
      <QueryState isLoading={isLoading} error={error}>
        {data && data.clusters.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No Receptors are configured for this Cortex. List them in CORTEX_RECEPTORS.
          </p>
        ) : (
          <>
            {data && <Filters clusters={data.clusters} search={search} onSearch={onSearch} />}
            <div className="space-y-4">
              {clusters.map(cluster => (
                <ClusterPanel
                  key={cluster.id}
                  cluster={cluster}
                  staleAfterSeconds={data!.staleAfterSeconds}
                  search={search}
                  rows={rows}
                  onSearch={onSearch}
                />
              ))}
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}

export function ClustersPage() {
  const search = useSearch({ from: '/clusters' });
  const navigate = useNavigate();
  return (
    <ClustersView
      search={search}
      onSearch={next => void navigate({ to: '/clusters', search: next, replace: true })}
    />
  );
}
